/**
 * SF-4996: a searchable company picker that loads the next page of companies when its dropdown
 * is scrolled near the bottom, so every company can be reached by scrolling alone. Used by the
 * Edit companies window (sf-api options route) and the Users page company filter (the companies
 * list route); each passes its own `fetchPage`.
 *
 * Paging: a page that came back full may have more after it; a shorter one is the last. A new
 * search starts again from the first page. A failed read offers no companies at all (never the
 * last good ones) and shows the error with a Retry beside the picker (in the parent's layout).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Select, Spin } from 'antd';
import type { SelectProps } from 'antd';
import { useOnError } from '@refinedev/core';
import { useInfiniteQuery } from '@tanstack/react-query';

export interface CompanyPageItem {
  id: string;
  name: string;
}

/** One page of companies matching `search`, starting `offset` companies in. */
export type FetchCompanyPage = (args: {
  search: string;
  offset: number;
  limit: number;
}) => Promise<CompanyPageItem[]>;

/** sf-api's default page for both routes (OPTIONS_DEFAULT_LIMIT and the companies list). */
export const COMPANY_PAGE_SIZE = 20;

// The options API clamps offsets to 10000; never repeatedly fetch that last page.
const MAX_COMPANY_OFFSET = 10000;
const SEARCH_DELAY_MS = 300;
// Start the next page this many pixels before the end, so it is usually there by the time the
// person reaches the bottom.
const NEAR_BOTTOM_PX = 40;

export type InfiniteCompanySelectProps = Omit<
  SelectProps,
  'options' | 'onSearch' | 'filterOption' | 'onPopupScroll' | 'notFoundContent' | 'showSearch'
> & {
  /** Cache key for this picker's pages; the search is added to it. */
  queryKey: readonly unknown[];
  fetchPage: FetchCompanyPage;
  /** Read only while true (for example, not while "All companies" is on). */
  enabled?: boolean;
  /** Companies not to offer (for example, ones the person already has). */
  hiddenIds?: ReadonlySet<string>;
};

export const InfiniteCompanySelect: React.FC<InfiniteCompanySelectProps> = ({
  queryKey,
  fetchPage,
  enabled = true,
  hiddenIds,
  ...selectProps
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const popup = useRef<HTMLDivElement>(null);
  const listHeight = selectProps.listHeight ?? 256;
  const listItemHeight = selectProps.listItemHeight ?? 32;
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Failures go to the auth provider like every useCustom read (a dead session logs out).
  const { mutate: checkError } = useOnError();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useInfiniteQuery<CompanyPageItem[]>({
    queryKey: [...queryKey, debouncedSearch],
    queryFn: ({ pageParam }) =>
      fetchPage({
        search: debouncedSearch,
        offset: typeof pageParam === 'number' ? pageParam : 0,
        limit: COMPANY_PAGE_SIZE,
      }),
    getNextPageParam: (last, pages) => {
      const offset = pages.reduce((n, p) => n + p.length, 0);
      return last.length === COMPANY_PAGE_SIZE && offset <= MAX_COMPANY_OFFSET ? offset : undefined;
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    // Refine keeps the previous key's data by default; a new search must not show the old one's.
    keepPreviousData: false,
    onError: (error) => checkError(error),
  });

  // TanStack keeps the pages it had after a failed read; never offer those stale choices.
  const failed = enabled && query.isError;
  const seen = new Set<string>();
  const options: { value: string; label: string }[] = [];
  if (!failed) {
    for (const company of query.data?.pages.flat() ?? []) {
      // A company added while paging shifts the rest by one, so a page can repeat the last
      // company of the one before.
      if (seen.has(company.id) || hiddenIds?.has(company.id)) continue;
      seen.add(company.id);
      options.push({ value: company.id, label: company.name });
    }
  }

  const { hasNextPage, isFetching, fetchNextPage } = query;
  const isOpen = selectProps.open ?? open;
  useEffect(() => {
    if (!isOpen || !enabled || failed || isFetching || !hasNextPage) return;
    const list = popup.current?.querySelector('.rc-virtual-list-holder');
    // Empty menus have no holder. Before layout, use the configured row/viewport sizes.
    const scrollable =
      list && list.clientHeight > 0
        ? list.scrollHeight > list.clientHeight
        : options.length * listItemHeight > listHeight;
    if (!scrollable) void fetchNextPage({ cancelRefetch: false });
  }, [
    isOpen,
    enabled,
    failed,
    isFetching,
    hasNextPage,
    fetchNextPage,
    options.length,
    query.data?.pages.length,
    listHeight,
    listItemHeight,
  ]);

  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    const nearBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - NEAR_BOTTOM_PX;
    if (!nearBottom || !query.hasNextPage) return;
    // Scroll events come faster than re-renders: while a read is running this joins it instead
    // of cancelling it and asking again.
    void query.fetchNextPage({ cancelRefetch: false });
  };

  let notFound: React.ReactNode = 'No matching companies';
  // A query that has not run yet (the picker just opened) is loading too, not "no matches".
  if (query.isLoading || (enabled && (query.isFetching || (!failed && hasNextPage)))) {
    notFound = <Spin size="small" />;
  } else if (failed) notFound = 'Could not load companies.';

  return (
    <>
      <Select
        {...selectProps}
        showSearch
        listHeight={listHeight}
        listItemHeight={listItemHeight}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          selectProps.onOpenChange?.(nextOpen);
        }}
        options={options}
        searchValue={search}
        onSearch={setSearch}
        onSelect={(value, option) => {
          setSearch('');
          setDebouncedSearch('');
          selectProps.onSelect?.(value, option);
        }}
        onClear={() => {
          setSearch('');
          setDebouncedSearch('');
          selectProps.onClear?.();
        }}
        filterOption={false}
        onPopupScroll={handlePopupScroll}
        notFoundContent={notFound}
        popupRender={(menu) => (
          <div ref={popup}>
            {menu}
            {query.isFetchingNextPage && (
              <div style={{ textAlign: 'center', padding: 8 }}>
                <Spin size="small" aria-label="Loading more companies" />
              </div>
            )}
          </div>
        )}
      />
      {failed && (
        <Alert
          type="error"
          showIcon
          message="Could not load companies. Try again."
          action={
            <Button size="small" loading={query.isFetching} onClick={() => void query.refetch()}>
              Retry
            </Button>
          }
        />
      )}
    </>
  );
};
